import classNames from "classnames";
import * as H from "history";
import { isEqual } from "lodash";
import * as React from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import { RouteRestrictionBoundary } from "@app/authorization";
import { ProjectModule } from "@app/projects/literals";
import {
  IconToggle as SkeletonIconToggle,
  PageHeader as SkeletonHeader,
  PrimaryTable as SkeletonTable,
} from "@app/shared/skeleton-components";
import { Flip, ToastPosition, toast } from "react-toastify";
import { MaintenanceAlert } from "@app/maintenance";
import { AccuracyGapAnalysisStatus, FilterType, ProjectPerspective, ProjectRunConfigurationStatus, ProjectRunConfigurationType } from "@app/projects/enumerators";
import { translateProjectChangeReason } from "@app/projects/projectChangeReason";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import RegionFilters from "@app/projects/regionFilters";
import { Details as AccuracyModule } from "@app/projects/network-assessment/accuracy/Details";
import { Details as AdequacyModule } from "@app/projects/network-assessment/adequacy/Details";
import { Charts, SkeletonCharts } from "@app/projects/network-assessment/Charts";
import { NetworkCoverageMap } from "@app/projects/network-assessment/maps/network-coverage-map/NetworkCoverageMap";
import { Options } from "@app/projects/network-assessment/Options";
import Publish from "@app/projects/network-assessment/Publish";
import ResourceControls from "@app/projects/network-assessment/ResourceControls";
import { SummaryRouter } from "@app/projects/network-assessment/summary";
import { AccuracyGapAnalysisContext, AccuracyGapAnalysisContextProvider } from "@app/projects/accuracy-gap-analysis/AccuracyGapAnalysisContext";
import { AgaAlert } from "@app/projects/network-assessment/adequacy/AgaAlert";
import PendingUnprocessedSpecialtyGroupExceptionsChangesSidePanelLink from "@app/projects/servicing-provider/PendingUnprocessedSpecialtyGroupExceptionsChangesSidePanelLink";
import { timeDistanceCalculatorTypeDisplayMap } from "@app/projects/timeDistanceCalculatorTypeDisplayMap";
import { FlyoutLink } from "@app/shared/Flyouts";
import { FeatureFlagContext, FeatureFlagContextProps } from "@app/feature-flags/FeatureFlagContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import ProviderHubLandingPage from "@app/projects/network-assessment/provider-hub/ProviderHubLandingPage";
import { JSX } from "react";
import { NetworkIntegrityPage } from '@app/projects/network-assessment/network-integrity';
import { getLastAppliedMembership, setLastAppliedMembership, setLastAppliedProviderType, setLastAppliedDisplayMode } from '@app/projects/network-assessment/network-integrity/NetworkIntegrityContext';
import { NetworkIntegrityDisplayMode, NetworkIntegrityMembership, NetworkIntegrityProviderTypes } from '@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants';
import { GapDensityMap } from "@app/projects/network-assessment/maps/gap-density-map/GapDensityMap";
import AIChatPanel from "@app/projects/network-assessment/provider-hub/ai-assistant/AIChatPanel";
import { QuinnFlyout } from "@app/projects/network-assessment/provider-hub/ai-assistant/QuinnFlyout";
import {
  isQuinnFlyoutLayout,
  userCanAccessAIChatPanel
} from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatPanelAuthorizationHelper";
import {
  AskQuinnButton
} from "@app/projects/network-assessment/provider-hub/ai-assistant/AskQuinnButton";
import {
  getAskQuinnButtonState
} from "@app/projects/network-assessment/provider-hub/ai-assistant/askQuinnButtonGate";
import { ViewableFieldsContainer } from "@app/viewable-fields/ViewableFieldsContainer";
import Features from "@app/feature-flags/features";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import ErrorBoundary from "@app/shared/ErrorBoundary";
import { formatDateTime } from "@app/shared/formatDateTime";
import Page from "@app/shared/Page";
import PageHeaderLeft from "@app/shared/PageHeaderLeft";
import PageSection from "@app/shared/PageSection";
import { RegionFilterItem } from "@app/projects/adequacySharedProjectContextProps";
import CountyProjectContainer from "@app/projects/CountyProjectContainer";
import { CountyProjectContextProps } from "@app/projects/CountyProjectContext";
import ProjectOptions from "@app/projects/projectOptions";
import { getProviderChangeRoleGate } from "@app/projects/providerChangeRoleGate";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESAlertsContainer } from "@app/shared/qes-standards/AlertsContainer";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContextProps, UserContext, UserInfo } from "@app/user/UserContext";
import { getGenericErrorMessage } from "@app/shared/utility/getGenericErrorMessage";
import { parseQuerystring } from "@app/shared/utility/parseQuerystring";
import { preventDefaultMouseEvent } from "@app/shared/utility/preventDefaultMouseEvent";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import TemplateService from "@app/templates/templateService";
import { AccuracyGapAnalysisUiProps } from "@app/projects/accuracy-gap-analysis/accuracyGapAnalysisUiProps";
import { getTimeString } from "@app/projects/network-assessment/adequacy/adequacyUtilities";
import { NetworkPerformancePage } from "@app/projects/network-assessment/network-performance";
import { ClaimsActivityRouter } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityRouter";
import { ClaimsInsightsRouter } from "@app/projects/network-assessment/pci-dashboard/claims-insights/ClaimsInsightsRouter";
import ProviderActionsDrawerFlyout from "@app/projects/network-assessment/provider-actions/ProviderActionsDrawerFlyout";
import { GapManagementDashboard } from "@app/projects/network-assessment/gap-management-dashboard/GapManagementDashboard";
import { AdequacySubmoduleEnum } from "@app/projects/network-assessment/adequacySubmoduleEnum";
import { GapRecordsTable } from "@app/projects/network-assessment/gap-management-dashboard/components/GapRecordsTableWidget";
import { NetworkIntegrityWidgetContainer } from "@app/projects/network-assessment/network-integrity/widgets/NetworkIntegrityWidgetContainer";

/** The props passed in via the route used to get here. */
interface CountyProjectOverviewRouteProps {
  projectId: string;
}

/** The combined props received by the project overview. */
type CountyProjectOverviewProps =
  RouteComponentProps<CountyProjectOverviewRouteProps> &
  UserContextProps &
  CountyProjectContextProps &
  AccuracyGapAnalysisUiProps &
  FeatureFlagContextProps;

interface CountyProjectOverviewState {
  activeProjectModule: ProjectModule;
  authorizedProjectModules: ProjectModule[];
  disableAgaRunBanner: boolean;
  filters: JSX.Element;
  showCharts: boolean;
  statusBanners: JSX.Element | JSX.Element[];
  selectedSubmodule: AdequacySubmoduleEnum;
}

const CountyProjectOverview: React.FC<RouteComponentProps<CountyProjectOverviewRouteProps>> = (routeProps: RouteComponentProps<CountyProjectOverviewRouteProps>) => {
  const projectId = Number(routeProps.match.params.projectId);
  return (
    <UserContext.Consumer>
      {(user) => {
        if (!user) {
          throw 403;
        }
        return (
          <ErrorBoundary user={user}>
            <RouteRestrictionBoundary user={user} {...routeProps}>
              <CountyProjectContainer projectId={projectId}>
                {(countyProjectContextProps) =>
                  <AccuracyGapAnalysisContextProvider project={countyProjectContextProps.project} projectRunConfigurationId={countyProjectContextProps.runConfiguration?.projectRunConfigurationId}
                    publishStatus={!!countyProjectContextProps.runConfiguration?.datePublished} runConfigurationstatus={countyProjectContextProps.runConfiguration?.status} userRoles={user.roles}>
                    <AccuracyGapAnalysisContext.Consumer>
                      {(agaProps: AccuracyGapAnalysisUiProps) => (
                        <FeatureFlagContext.Consumer>
                          {(featureFlagProps: FeatureFlagContextProps) => (
                            <CountyProjectOverviewContent user={user} {...countyProjectContextProps} {...routeProps} {...agaProps} {...featureFlagProps} />
                          )}
                        </FeatureFlagContext.Consumer>
                      )}
                    </AccuracyGapAnalysisContext.Consumer>
                  </AccuracyGapAnalysisContextProvider>
                }
              </CountyProjectContainer>
            </RouteRestrictionBoundary>
          </ErrorBoundary>
        )
      }}
    </UserContext.Consumer>
  );
};

export default CountyProjectOverview;

/** Renders an overview page for a single project. */
export class CountyProjectOverviewContent extends React.Component<CountyProjectOverviewProps, CountyProjectOverviewState> {
  private templateService = new TemplateService();
  private claimsActivityModulePath = "claims-activity";
  private claimsInsightsModulePath = "claims-insights";
  private summaryModulePath = "summary";
  private mappingModulePath = "mapping";
  private providerHubModulePath = "provider-hub";
  /** Not a real module, but a subpage of Provider Hub that has a module-like tab */
  private providerClaimsInsightsModulePath = "provider-claims-insights";
  private networkIntegrityModulePath = "network-integrity";
  private networkPerformanceModulePath = "network-performance";

  /** The module/view (tab) we're currently working with. */
  private get module() {
    return this.getModuleForPath(this.props.location.pathname);
  }

  private getModuleForPath(path: string): ProjectModule {
    let module: ProjectModule = null;

    if (path.includes("accuracy")) {
      module = "Accuracy";
    } else if (path.includes("adequacy")) {
      module = "Adequacy";
    } else if (path.includes(this.summaryModulePath)) {
      module = "Summary";
    } else if (path.includes(this.mappingModulePath)) {
      module = "Mapping";
    } else if (path.includes(this.providerHubModulePath)) {
      module = "Provider-Hub"
    } else if (path.includes(this.claimsActivityModulePath)) {
      module = "Claims-Activity";
    } else if (path.includes(this.claimsInsightsModulePath)) {
      module = "Claims-Insights"
    } else if (path.includes(this.networkIntegrityModulePath)) {
      module = "Network-Integrity";
    } else if (path.includes(this.networkPerformanceModulePath)) {
      module = "Network-Performance";
    }

    return module;
  }

  /** Whether the URL is on the "Provider Claims Insights" pseudo-module. */
  private get isOnProviderClaimsInsightsRoute(): boolean {
    return this.props.location.pathname.includes(`${this.providerHubModulePath}/${this.providerClaimsInsightsModulePath}`);
  }

  private get shouldShowAdequacyTab(): boolean {
    const { runConfiguration } = this.props;

    return !!runConfiguration
      && !!runConfiguration.adequacyTemplateId
      && (runConfiguration.status === ProjectRunConfigurationStatus.Succeeded
        || runConfiguration.status === ProjectRunConfigurationStatus.FailedProviderChanges);
  }

  /**
   * Returns undefined only while the project's run configurations have not loaded, so callers that redirect
   * on a negative answer can avoid acting on a run whose option is merely not known yet. Once the collection
   * is loaded, a run that is absent from it resolves to false rather than staying unknown.
   */
  private get networkIntegrityDashboardRunOption(): boolean | undefined {
    const { project, runConfiguration } = this.props;

    if (!runConfiguration || !project?.projectRunConfigurations?.length) return undefined;

    return project.projectRunConfigurations
      .find((prc) => prc.projectRunConfigurationId == runConfiguration.projectRunConfigurationId)
      ?.showNetworkIntegrityDashboard ?? false;
  }

  private get networkIntegrityDashboardProjectOption(): boolean {
    return this.networkIntegrityDashboardRunOption === true;
  }

  private get canShowSummary(): boolean {
    const { regionFilters, runConfiguration } = this.props;

    return this.shouldShowAdequacyTab
      && !!runConfiguration
      && (runConfiguration.status === ProjectRunConfigurationStatus.Succeeded
        || runConfiguration.status === ProjectRunConfigurationStatus.FailedProviderChanges)
      && (!regionFilters || !(regionFilters.state || regionFilters.county || regionFilters.specialty))
      && !this.networkIntegrityDashboardProjectOption;
  }

  private get canShowNetworkIntegrity(): boolean {
    const { runConfiguration } = this.props;

    return !!runConfiguration
      && runConfiguration.adequacyTemplateCollectionId != null
      && runConfiguration.status === ProjectRunConfigurationStatus.Succeeded
      && this.networkIntegrityDashboardProjectOption
      && !runConfiguration.isTermed
      && !runConfiguration.isProjected
      && !runConfiguration.isProjectedTermed;
  }

  private canRenderStatusToast = true;
  private statusContainerRef = React.createRef<HTMLDivElement>();
  private statusToastId: string | number;

  constructor(props: CountyProjectOverviewProps) {
    super(props);

    this.state = {
      activeProjectModule: this.module,
      authorizedProjectModules: ["Adequacy"], // at the moment, every project automatically has "adequacy" checked. therefore, we do not have to wait for the backend call to determine whether or not the user has the appropriate permissions granted for this tab
      disableAgaRunBanner: false,
      filters: null,
      selectedSubmodule: AdequacySubmoduleEnum.TrendChart,
      showCharts: true,
      statusBanners: null,
    };
  }

  public async componentDidMount() {
    window.addEventListener("scroll", this.renderStatusToast);
  }

  public async componentDidUpdate(prevProps: CountyProjectOverviewProps) {
    let newState: Partial<CountyProjectOverviewState> = {};

    const activeProjectModule: ProjectModule = this.module;
    const askQuinnInBreadcrumbs = isQuinnFlyoutLayout(this.props.enabledFeatureFlags);

    // Quinn's context is reconciled before the awaited work below: renderStatus fetches template
    // metadata, and nothing here catches a rejection, so leaving these behind the await would strand
    // the chat pointed at a provider or a run the user has already navigated away from.
    if (askQuinnInBreadcrumbs) {
      // With the breadcrumb-bar button, Quinn is a project-level panel and stays open across tabs.
      // The provider/contract-entity context, however, is only meaningful inside Provider Hub:
      // ProviderHubContext maintains it while mounted but does not clear it on unmount, so without
      // this the chat would keep telling Quinn the user is "viewing" a provider they have navigated
      // away from. Compared against the previous route (not this.state.activeProjectModule) because
      // setActiveProjectModule updates state and history together, so a tab click never desyncs them
      // and the module-changed check below would never see a difference to react to.
      if (this.getModuleForPath(prevProps.location.pathname) === "Provider-Hub" && activeProjectModule !== "Provider-Hub") {
        this.props.setVirtualAssistantContext({
          servicingProviderKey: undefined,
          servicingProviderName: undefined,
          contractEntityId: undefined,
          contractEntityName: undefined,
        });
      }

      // QuinnFlyout closes itself when the Ask Quinn gate turns against an open panel, but a run
      // configuration going away unmounts it before that effect can run, leaving isOpen set. The
      // next run configuration would then mount the flyout straight into the open state -- loading a
      // conversation -- for a run the user never opened Quinn for.
      if (prevProps.runConfiguration && !this.props.runConfiguration && this.props.virtualAssistantContext.isOpen) {
        this.props.setVirtualAssistantContext({ isOpen: false });
      }
    }

    if (!isEqual(prevProps.user, this.props.user) ||
      !isEqual(prevProps.project, this.props.project) ||
      !isEqual(prevProps.runConfiguration, this.props.runConfiguration)) {
      const newStatusBanners = await this.renderStatus(this.props.user, this.props.project.resource.isShared, this.props.project.isRunning, this.props.processProviderChanges, this.props.project, this.props.runConfiguration, this.props.runProject, this.props.requeueMarketAndProspectNCP, this.props.forceUpdateProject, this.props.project.isReadOnly, this.props.project.isEditRestricted);
      newState = {
        ...newState,
        statusBanners: newStatusBanners,
      };
    }

    if (!isEqual(prevProps.filters, this.props.filters) ||
      !isEqual(prevProps.history.location, this.props.history.location)) {
      const newFilters = this.renderFilters(this.props.filters, this.props.removeFilter, this.props.removeCountyFilter);
      newState = {
        ...newState,
        filters: newFilters,
      };
    }

    if (this.props.runConfiguration) {
      //set the project module if it's not already set or fix it if it's on an invalid module
      if (this.state.activeProjectModule === null && this.props.runConfiguration) {
        if (this.props.runConfiguration.adequacyTemplateCollectionId) {
          this.setActiveProjectModule("Adequacy", true);
        } else {
          this.setActiveProjectModule("Accuracy", true);
        }
      } else if (this.state.activeProjectModule === "Adequacy" && !this.props.runConfiguration.adequacyTemplateCollectionId) {
        this.setActiveProjectModule("Accuracy", true);
      } else if ((this.state.activeProjectModule === "Accuracy" && !this.props.runConfiguration.accuracyTemplateCollectionId) || ((this.state.activeProjectModule === "Claims-Activity" || this.state.activeProjectModule === "Claims-Insights") && (!this.props.runConfiguration.showPciDashboard || (!this.props.runConfiguration.showPciData)))) {
        this.setActiveProjectModule("Adequacy", true);
      } else if (this.state.activeProjectModule === "Mapping") {
        // If this is an adequacy project and the time slice is not published,
        // or viewing opportunity counties, force the user back to the adequacy tab
        if (this.props.runConfiguration.adequacyTemplateCollectionId) {
          const { runConfiguration } = this.props;

          if (
            runConfiguration.isProjected ||
            runConfiguration.isTermed ||
            runConfiguration.isProjectedTermed
          ) {
            this.setActiveProjectModule("Adequacy", true);
          }
        } else if (this.props.runConfiguration.accuracyTemplateCollectionId) {
          // cannot navigate from accuracy to mapping
          this.setActiveProjectModule("Accuracy", true);
        }
      } else if (this.state.activeProjectModule === "Provider-Hub") {
        // Adequacy or Blended Project should use Adequacy Logic if they should use Provider hub or not.
        if (!!this.props.runConfiguration.adequacyTemplateCollectionId) {
          if (!this.props.runConfiguration.isLatestPublished ||
            (!(this.props.enabledFeatureFlags.has(Features.PROVIDER_HUB) && (this.props.user.roles.includes(Roles.PROVIDER_HUB_PREVIEW) || this.props.enabledFeatureFlags.has(Features.PROVIDER_HUB_PUBLIC))))) {
            this.setActiveProjectModule("Adequacy", true);
          }
        } else if (!!this.props.runConfiguration.accuracyTemplateCollectionId) {
          this.setActiveProjectModule("Accuracy", true);
        }
      } else if (this.state.activeProjectModule === "Network-Integrity"
        // Only redirect once the run's dashboard option is actually known. The project loads after the run
        // configuration on a remount (e.g. returning here from a provider detail page), and redirecting on
        // that transient sent the user to Summaries instead of the dashboard.
        && this.networkIntegrityDashboardRunOption !== undefined
        && !this.canShowNetworkIntegrity) {
        this.setActiveProjectModule(this.canShowSummary ? "Summary" : "Adequacy", true);
      }
    }

    if (Object.keys(newState).length) {
      this.setState((prevState) => ({
        ...prevState,
        ...newState,
      }), () => {
        if (!!newState.statusBanners) {
          this.canRenderStatusToast = true;
        }
      });
    }

    // corrects the active project module in case the back button is used after switching between tabs
    if (activeProjectModule !== this.state.activeProjectModule) {
      this.setState({
        activeProjectModule,
      });
    }

    // Legacy behaviour: Quinn is reachable only from inside Provider Hub, so leaving it closes the
    // panel outright. Retire this branch along with AIAdequacySummary.
    if (!askQuinnInBreadcrumbs && this.props.virtualAssistantContext.isOpen && activeProjectModule !== "Provider-Hub") {
      this.props.setVirtualAssistantContext({ isOpen: false, servicingProviderKey: undefined, servicingProviderName: undefined });
    }

    if (!isEqual(prevProps.runConfiguration?.accuracyGapAnalysisStatus, this.props.runConfiguration?.accuracyGapAnalysisStatus) &&
      this.props.runConfiguration?.accuracyGapAnalysisStatus == AccuracyGapAnalysisStatus.Failed || this.props.runConfiguration?.accuracyGapAnalysisStatus == AccuracyGapAnalysisStatus.Canceled)
      this.setState({
        disableAgaRunBanner: false,
      });
  }

  public componentWillUnmount() {
    window.removeEventListener("scroll", this.renderStatusToast);
    this.removeStatusToast();
    if(this.props.history.location.pathname === '/'){
      setLastAppliedDisplayMode(undefined);
      setLastAppliedProviderType(undefined);
      setLastAppliedMembership(undefined);
    }
  }

  public render() {
    const query = parseQuerystring(this.props.location.search);
    const showProjectOptions = query.showProjectOptions && query.showProjectOptions === "true" || false;

    return this.renderOverview(
      this.props.error as JQuery.jqXHR<unknown>,
      this.props.loading,
      this.props.project,
      this.props.runConfiguration,
      showProjectOptions,
      this.props.user,
      this.props.perspective,
      this.props.breadcrumbItems,
      this.props.hideProjectHeader
    );
  }

  public resetStatusBanners = async (hasUnprocessedTerminations: boolean, hasUnprocessedTargets: boolean, hasUnprocessedExcludes: boolean = false): Promise<void> => {
    let newState: Partial<CountyProjectOverviewState> = {};
    if (this.props.project) {
      const updatedProject = {
        ...this.props.project,
        hasUnprocessedExcludedProviders: hasUnprocessedExcludes,
        hasUnprocessedProjectionProviders: hasUnprocessedTargets || hasUnprocessedExcludes,
        hasUnprocessedProviderTerminations: hasUnprocessedTerminations,
        hasUnprocessedTargetProviders: hasUnprocessedTargets,
      };

      this.props.setProjectProjectionFlags(hasUnprocessedTerminations, hasUnprocessedTargets, hasUnprocessedExcludes);

      const newStatusBanners = await this.renderStatus(this.props.user, this.props.project.resource.isShared, this.props.project.isRunning, this.props.processProviderChanges, updatedProject, this.props.runConfiguration, this.props.runProject, this.props.requeueMarketAndProspectNCP, this.props.forceUpdateProject, this.props.project.isReadOnly, this.props.project.isEditRestricted);
      newState = {
        ...newState,
        statusBanners: newStatusBanners,
      };

      if (Object.keys(newState).length) {
        this.setState((prevState) => ({
          ...prevState,
          ...newState,
        }), () => {
          if (!!newState.statusBanners) {
            this.canRenderStatusToast = true;
          }
        });
      }
    }
  }

  /* this function gets called on load after each */
  /* project module's authorization has been determined to be true */
  public handleAuthorizationUpdate = (permitLookup: string): void => {
    if (permitLookup === "VIEW_ACCURACY_TAB") {
      this.setState((prevState) => ({
        ...prevState,
        authorizedProjectModules: prevState.authorizedProjectModules.concat("Accuracy"),
      }));
    }

    if (this.state.activeProjectModule === null) {
      // only resort to setting a default tab
      // if the url on load path does not contain a project module
      // this will not be triggered at the moment,
      // due to the fact that adequacy is currently automatically checked
      // and does not require a backend call to determine accessibility
      this.determineDefaultProjectModule();
    }
  }

  private removeStatusToast = () => {
    if (!!this.statusToastId) {
      this.canRenderStatusToast = false;
      toast.dismiss(this.statusToastId);
      this.statusToastId = undefined;
    }
  }

  private renderStatusToast = () => {
    if (this.statusContainerRef.current && this.statusContainerRef.current.querySelectorAll(".qes-alert").length > 0 && this.canRenderStatusToast) {
      const statusContainer = this.statusContainerRef.current;
      const offScreen = statusContainer.getBoundingClientRect().bottom < (16 * 3.5); // 3.5rem, the height of looper's nav bar
      const toastSettings = {
        autoClose: false as const, // TS said it expects "number | false" and a boolean wasn't acceptable, so...
        closeButton: true,
        closeOnClick: true,
        containerId: "z-index-1030",
        hideProgressBar: true,
        // The -110 is somewhat arbitrary, but it should scroll up over the project title
        onClick: () => window.scrollTo({ behavior: "smooth", top: statusContainer.getBoundingClientRect().top + document.documentElement.scrollTop - 110 }),
        position: "top-center" as ToastPosition,
        transition: Flip,

      };
      if (offScreen) {
        if (!this.statusToastId) {
          const childClasses = Array.from(statusContainer.querySelectorAll(".qes-alert")).reduce((result, child) => result += child.className, "");
          const toastString = "Click here to view project notifications.";
          if (childClasses.includes("danger")) {
            this.statusToastId = toast.error(toastString, toastSettings);
          } else if (childClasses.includes("warning")) {
            this.statusToastId = toast.warn(toastString, toastSettings);
          } else {
            this.statusToastId = toast.info(toastString, toastSettings);
          }
        }
      } else {
        this.removeStatusToast();
      }
    } else {
      this.removeStatusToast();
    }
  }

  /**
   * Takes the current path to produce a new path using @newModule.
   * Returns a path ready to be used in props.history.push.
   * @param newModule
   */
  private getNewPath = (location: H.Location, newModule: string, removeSearch = false): string => {
    const path = location.pathname;
    const oldPath = path.split("/");
    const oldModule = oldPath[3];
    let newPath: string;
    if (oldPath.length > 4 && this.state.activeProjectModule === null) {
      // if the url path contains "drilled in" parameters, but no project module,
      // remove everything to the right of where the project module should appear,
      // (checking to see if the entire path is valid is rather messy)
      // and resort to the default tab for the user
      newPath = oldPath[0] + "/" + oldPath[1] + "/" + oldPath[2] + "/" + newModule;
    } else {
      if (oldModule === undefined) {
        // if the url path does not include a project module
        // or any other parameter where the project module should be, add it
        const prefixedNewModule = "/" + newModule;
        newPath = path.concat(prefixedNewModule);
      } else {
        // insert the new module
        newPath = `${oldPath.slice(0, 3).join("/")}/${newModule}`;

        const shouldAppendParameters =
          oldPath.length > 4 &&
          !(oldModule === this.summaryModulePath || newModule === this.summaryModulePath || oldModule === this.claimsActivityModulePath || newModule === this.claimsActivityModulePath || oldModule === this.claimsInsightsModulePath || newModule === this.claimsInsightsModulePath || oldModule === this.providerHubModulePath) &&
          (!path.match(/inspection/i)) && // Inspection shares no commonality elsewhere, so ignore it on module nav.
          newModule != "provider-hub" && //When going to the ProviderHub tab, we should not append any parameter
          newModule != this.networkIntegrityModulePath; // NI reads filters from context, not URL

        // To resolve issues when moving between maps and adequacy while using an Opportunity County
        const shouldAppendQueryString = newModule === "mapping";

        if (shouldAppendParameters) {
          //keep drilled in parameters if they exist when we switch between modules
          newPath += `/${oldPath.slice(4).join("/")}`;
        } else if (oldModule === this.networkIntegrityModulePath && newModule === "adequacy") {
          // Use return filters computed by NI FilterContainer, falling back to original Adequacy filters
          const locationState = location.state as { returnRegionFilters?: RegionFilters } | undefined;
          const returnFilters = locationState?.returnRegionFilters ?? this.props.regionFilters;
          const filterPath = Object.entries(returnFilters)
            .filter(([, value]) => !!value)
            .map(([key, value]) => `${key}/${encodeURIComponent(value)}`)
            .join("/");
          if (filterPath) {
            newPath += `/${filterPath}`;
          }
        }

        if (shouldAppendQueryString) {
          newPath += location.search;
        }
      }
    }

    return newPath + (removeSearch ? "" : location.search);
  }

  private determineDefaultProjectModule = () => {
    // the priority order for default tabs is as follows:
    // adequacy > accuracy
    if (this.state.authorizedProjectModules.find((x) => x === "Adequacy")) {
      // default tab to adequacy
      this.setActiveProjectModule("Adequacy");
    } else if (this.state.authorizedProjectModules.find((x) => x === "Accuracy")) {
      // default tab to accuracy
      this.setActiveProjectModule("Accuracy");
    }
  }

  private renderOverview(
    error: JQuery.jqXHR,
    loading: boolean,
    project: ProjectOptions,
    runConfiguration: ProjectRunConfigurationResult,
    showProjectOptions: boolean,
    user: UserInfo,
    perspective: ProjectPerspective,
    breadcrumbItems: (string | Breadcrumb)[],
    hideProjectHeader: boolean,
  ) {

    const previewUser = user.roles.indexOf(Roles.PREVIEW) > -1;

    if (project && !error) {
      const isShared = project.resource.isShared;
      const isPublished = (runConfiguration && runConfiguration.datePublished ? true : false);
      const haveAccess = previewUser || (!isShared || (isShared && isPublished) ? true : false);
      const shouldShowAccuracyTab = runConfiguration && runConfiguration.accuracyTemplateId && (runConfiguration.status === ProjectRunConfigurationStatus.Succeeded);
      const shouldShowAdequacyTab = this.shouldShowAdequacyTab;
      const showSummary = this.canShowSummary;
      const showMapping = shouldShowAdequacyTab

        //Simulated Timesliced are not supported in Mapping
        && !runConfiguration.isProjected
        && !runConfiguration.isProjectedTermed
        && !runConfiguration.isTermed

        // Dual Membership Compare Mode ("Both") is not supported, unless viewing via the Maps
        // membership-type dropdown -- the map itself defaults to Primary in that case
        && (!this.props.isDualMembershipMode || this.props.enabledFeatureFlags.has(Features.DUAL_MEMBERSHIP_FOR_MAPS))

        // Secondary Memberset is not supported, unless viewing it via the Maps membership-type dropdown
        && (runConfiguration.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental
          || this.props.enabledFeatureFlags.has(Features.DUAL_MEMBERSHIP_FOR_MAPS))

        // Compare Mode is not supported -- except the Dual Membership ("Both") flavor above,
        // which also populates compareToRunConfiguration with the secondary companion
        && (!this.props.compareToRunConfiguration
          || (this.props.isDualMembershipMode && this.props.enabledFeatureFlags.has(Features.DUAL_MEMBERSHIP_FOR_MAPS)))

        // Accuracy and Blended projects are not supported
        && runConfiguration.accuracyTemplateId == null;

      const showPciDashboard = runConfiguration?.status === ProjectRunConfigurationStatus.Succeeded
        && runConfiguration?.showPciData
        && runConfiguration?.showPciDashboard
        && !!runConfiguration.adequacyTemplateCollectionId
        && !!runConfiguration.datePublished
        && !!runConfiguration.isLatestPublished
        && !runConfiguration.isTermed
        && !runConfiguration.isProjected
        && !runConfiguration.isProjectedTermed
        && !this.props.isDualMembershipMode
        && !this.props.compareToRunConfiguration
        && !project.isReadOnly;
      const showProviderHub = shouldShowAdequacyTab
        && runConfiguration.isLatestPublished
        && !runConfiguration.isTermed
        && !runConfiguration.isProjected
        && !runConfiguration.isProjectedTermed
        && !this.props.isDualMembershipMode
        && (!this.props.compareToRunConfiguration)
        && this.props.enabledFeatureFlags.has(Features.PROVIDER_HUB)
        && runConfiguration.isServicingProviderInSnowflake
        && (user.roles.includes(Roles.PROVIDER_HUB_PREVIEW) || this.props.enabledFeatureFlags.has(Features.PROVIDER_HUB_PUBLIC));
      const showProviderHubPciDashboard = this.props.enabledFeatureFlags.has(Features.ENABLE_SNOWFLAKE_PCI_DASHBOARD)
        && showPciDashboard
        && showProviderHub;
      const showNetworkIntegrity = this.canShowNetworkIntegrity;

      const statuses = (!loading || null) && (this.state.statusBanners);

      const olderRunConfiguration = !!this.props.compareToRunConfiguration ?
        this.props.compareToRunConfiguration.dateCreated > runConfiguration?.dateCreated ?
          runConfiguration :
          this.props.compareToRunConfiguration :
        runConfiguration;

      const newerRunConfiguration = !!this.props.compareToRunConfiguration && !this.props.isDualMembershipMode ?
        olderRunConfiguration === runConfiguration ?
          this.props.compareToRunConfiguration :
          runConfiguration :
        undefined;

      // Resolves to "hidden" whenever AIAdequacySummary is off, which keeps the breadcrumb bar and
      // the panel mount on their legacy behaviour. Computed once and shared by both.
      const askQuinnState = getAskQuinnButtonState({
        enabledFeatureFlags: this.props.enabledFeatureFlags,
        roles: user.roles,
        clientType: user.clientType,
        project,
        runConfiguration,
        isDualMembershipMode: this.props.isDualMembershipMode,
        compareToRunConfiguration: this.props.compareToRunConfiguration,
        activeProjectModule: this.state.activeProjectModule,
      });

      // As a flyout Quinn floats over the page, so the page keeps its full width and neither of the
      // side-by-side classes is applied. Both of those and the `d-flex` row retire with the flag.
      const quinnIsFlyout = isQuinnFlyoutLayout(this.props.enabledFeatureFlags);
      const showQuinnRail = this.props.virtualAssistantContext.isOpen && !quinnIsFlyout;

      if (haveAccess) {
        return (
          <div className={classNames({ "d-flex": showQuinnRail })}>
            <Page documentTitle={project?.resource.name || "Loading"} className={classNames({
              "county-project-overview__show-virtual-assistant": showQuinnRail
            })}>
              {/* actions is passed only when the button will actually draw: a rendered-null element
                  is still truthy, which would apply the bar's action layout classes with the flag off. */}
              <Breadcrumbs
                template={BreadcrumbTemplateEnum.NetworkAssessment}
                items={breadcrumbItems}
                actions={askQuinnState.kind !== "hidden" ? <AskQuinnButton state={askQuinnState} /> : undefined}
              />
              <PageInner>
                {!hideProjectHeader && (
                  <PageHeader title={project.resource.name} disableHR={true}>
                    <PageHeaderLeft>
                      <span className="btn-group">
                        <ResourceControls
                          defaultProjectOptionsOpen={showProjectOptions}
                          resourceId={project.projectId}
                          isArchived={project.resource.isArchived}
                          isProjectRunning={project.isRunning}
                          isReadOnly={project.isReadOnly}
                          isShared={project.resource.isShared}
                          projectName={project.resource.name}
                          user={this.props.user}
                        />
                      </span>
                    </PageHeaderLeft>
                  </PageHeader>
                )}
                <PageSection>
                  <ViewableFieldsContainer projectRunConfigurationId={runConfiguration ? runConfiguration.projectRunConfigurationId : null} customProjectId={project.projectId} specialtyGroupCode={this.props.filters.find(f => f.filterType == FilterType.Specialty)?.filterValue ?? ""}>
                    <div>
                      {!hideProjectHeader && (
                        <>
                          <div className={classNames("row", { mt20: !runConfiguration }, "project-status-container")} ref={this.statusContainerRef}>
                            <div className="col-12">
                              {statuses}
                              {user.showMaintenanceAlert &&
                                <MaintenanceAlert
                                  maintenanceTime={user.maintenanceTime}
                                />
                              }
                            </div>
                          </div>
                          {this.state.activeProjectModule !== "Network-Integrity" && this.state.filters}
                        </>
                      )}
                      {runConfiguration?.status !== ProjectRunConfigurationStatus.Canceled && (
                        <div className="row">
                          <div className="col-12">
                            {!hideProjectHeader && (
                              <div className="tabs-style-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <ul className="nav nav-tabs" role="menu">
                                    {showNetworkIntegrity && (
                                      <li className="nav-item" role="none"><a className={this.state.activeProjectModule === "Network-Integrity" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => { this.setActiveProjectModule("Network-Integrity"); })}>Network Integrity</a></li>
                                    )}
                                    {shouldShowAccuracyTab && (
                                      <li className="nav-item" role="none"><a className={this.state.activeProjectModule === "Accuracy" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => { this.setActiveProjectModule("Accuracy"); this.setState((prevState) => ({ ...prevState, showTrendChart: true })) })}>Accuracy</a></li>
                                    )}
                                    {(shouldShowAdequacyTab || null) && (
                                      <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Adequacy" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Adequacy"))}>Adequacy</a></li>
                                    )}
                                    {(showSummary || null) && (
                                      <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Summary" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Summary"))}>Summaries</a></li>
                                    )}
                                    {(showMapping || null) && (
                                      <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Mapping" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Mapping"))}>Maps</a></li>
                                    )}
                                    {(showProviderHub || null) && (
                                      <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Provider-Hub" && !this.isOnProviderClaimsInsightsRoute ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Provider-Hub"))}>Provider Hub</a></li>
                                    )}
                                    {showProviderHubPciDashboard && (
                                      // Link to PCI Dashboard subpage of Provider Hub
                                      <li role="none" className="nav-item"><a className={this.isOnProviderClaimsInsightsRoute ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Provider-Hub", false, `/${this.providerClaimsInsightsModulePath}`))}>Provider Claims Insights</a></li>
                                    )}
                                    {!showProviderHubPciDashboard && showPciDashboard && (
                                      <React.Fragment>
                                        <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Claims-Activity" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Claims-Activity"))}>Provider Claims Activity</a></li>
                                        <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Claims-Insights" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Claims-Insights"))}>Effectiveness and Efficiency</a></li>
                                      </React.Fragment>
                                    )}
                                    {(this.props.enabledFeatureFlags.has(Features.NETWORK_PERFORMANCE_NEW_MEASURES)) && (
                                      <li role="none" className="nav-item"><a className={this.state.activeProjectModule === "Network-Performance" ? "nav-link active" : "nav-link"} href="#" role="menuitem" onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => this.setActiveProjectModule("Network-Performance"))}>Network Performance</a></li>
                                    )}
                                  </ul>
                                  {(this.state.activeProjectModule !== "Mapping" && this.state.activeProjectModule !== "Claims-Activity" && this.state.activeProjectModule !== "Claims-Insights" && this.state.activeProjectModule !== "Provider-Hub" && this.state.activeProjectModule !== "Network-Integrity") && (
                                    <div className="d-flex flex-column">
                                      {runConfiguration?.timeDistanceCalculatorType && <p className="mb-2">{!!newerRunConfiguration && getTimeString(olderRunConfiguration, this.props.runFrequency)} Distance Method: <b>{timeDistanceCalculatorTypeDisplayMap[olderRunConfiguration?.timeDistanceCalculatorType]}</b></p>}
                                      {!!newerRunConfiguration && <p className="mb-0">{getTimeString(newerRunConfiguration, this.props.runFrequency)} Distance Method: <b>{timeDistanceCalculatorTypeDisplayMap[newerRunConfiguration?.timeDistanceCalculatorType]}</b></p>}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="container-fluid px-0">
                              {this.state.activeProjectModule === "Adequacy" && this.props.project.showAccuracyGapAnalysis && this.props.runConfiguration && this.props.runConfiguration.showAccuracyGapAnalysis &&
                                (this.renderAgaStatus(user, this.props))}
                              {(this.state.activeProjectModule !== "Summary" &&
                                this.state.activeProjectModule !== "Mapping" &&
                                this.state.activeProjectModule !== "Claims-Activity" &&
                                this.state.activeProjectModule !== "Claims-Insights" &&
                                this.state.activeProjectModule !== "Provider-Hub" &&
                                this.state.activeProjectModule !== "Network-Integrity" &&
                                this.state.activeProjectModule !== "Network-Performance") && (
                                  <>
                                    <Options
                                      disabled={loading || project.isRunning}
                                      showGraphicalArea={this.state.showCharts}
                                      onShowGraphicalAreaClick={this.handleShowCharts}
                                      defaultProjectOptionsOpen={showProjectOptions}
                                      isReadOnly={project.isReadOnly || project.isEditRestricted}
                                      hasProspectOrMarketProviders={this.props.hasProspectOrMarketProviders}
                                      onSelectedSubmoduleChange={this.onSubmoduleChange}
                                      selectedSubmodule={this.state.selectedSubmodule}
                                      showTrendChartToggle={this.state.showCharts && this.state.activeProjectModule === "Adequacy"}
                                      adequacyFilters={this.props.adequacyFilters}
                                    />
                                    {(this.state.showCharts || null) &&
                                      <>
                                        {this.state.selectedSubmodule === AdequacySubmoduleEnum.TrendChart && (
                                          <Charts {...this.props} module={this.state.activeProjectModule} />
                                        )}
                                        {this.renderGapDensityMap()}
                                        {this.state.selectedSubmodule === AdequacySubmoduleEnum.GapManagement && this.props.enabledFeatureFlags.has(Features.GAP_MANAGEMENT_DASHBOARD) && (
                                          <GapManagementDashboard />
                                        )}
                                      </>
                                    }
                                  </>
                                )}

                              <div className="row">
                                <div className="col-12">
                                  <div className="tabs-style-2">
                                    <div className="tab-content overview-tab-content">
                                      <Switch>
                                        <Route path={`${this.props.match.path}/accuracy`} component={AccuracyModule} />
                                        <Route path={`${this.props.match.path}/adequacy`} component={this.state.selectedSubmodule === AdequacySubmoduleEnum.GapManagement ? GapRecordsTable : AdequacyModule} />
                                        <Route path={`${this.props.match.path}/summary`} component={SummaryRouter} />
                                        {
                                          <Route path={`${this.props.match.path}/mapping`}
                                            render={(routeProps) => (
                                              <NetworkCoverageMap
                                                {...routeProps}
                                                processProviderChanges={this.props.processProviderChanges}
                                                setProjectHasUnprocessedProjectionProviders={this.props.setProjectHasUnprocessedProjectionProviders}
                                              />
                                            )}
                                          />
                                        }
                                        <Route exact={true} path={`${this.props.match.path}/provider-hub`} component={ProviderHubLandingPage} />
                                        {showProviderHubPciDashboard && (
                                          <Route exact={true} path={`${this.props.match.path}/provider-hub/${this.providerClaimsInsightsModulePath}`} component={ProviderHubLandingPage} />
                                        )}
                                        <Route path={`${this.props.match.path}/provider-hub/run/:runConfigurationId/provider-profile/:providerKey`} component={ProviderHubLandingPage} />
                                        <Route path={`${this.props.match.path}/provider-hub/run/:runConfigurationId/contract-entity-profile/:contractEntityId`} component={ProviderHubLandingPage} />
                                        <Route path={`${this.props.match.path}/provider-hub/run/:runConfigurationId/organization-profile/:organizationNpi/:contractEntityId`} component={ProviderHubLandingPage} />
                                        <Route path={`${this.props.match.path}/provider-hub/run/:runConfigurationId/hospital-profile/:ccn`} component={ProviderHubLandingPage} />
                                        {!showProviderHubPciDashboard && showPciDashboard && (
                                          <Route
                                            path={`${this.props.match.path}/claims-activity`}
                                            render={(props: RouteComponentProps) => (
                                              <ClaimsActivityRouter {...props} />
                                            )}
                                          />
                                        )}
                                        {!showProviderHubPciDashboard && showPciDashboard && (
                                          <Route
                                            path={`${this.props.match.path}/claims-insights`}
                                            render={(props: RouteComponentProps) => (
                                              <ClaimsInsightsRouter {...props} />
                                            )}
                                          />
                                        )}
                                        <Route
                                          path={`${this.props.match.path}/${this.networkIntegrityModulePath}`}
                                          component={NetworkIntegrityPage}
                                        />
                                        <Route
                                          path={`${this.props.match.path}/${this.networkPerformanceModulePath}`}
                                          component={NetworkPerformancePage}
                                        />
                                      </Switch>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </ViewableFieldsContainer>
                </PageSection>
              </PageInner>
            </Page>
            {quinnIsFlyout
              ? !!runConfiguration && (
                // Mounted whenever there is a run to ask about, rather than only while Quinn is open
                // or the gate allows it: QuinnFlyout owns the open/closed lifecycle so the panel can
                // survive its own close animation, including when the gate turns against a flyout
                // that is already open. It renders nothing until Quinn is opened. A project with no
                // run configuration has nothing to query and no id to pass.
                <QuinnFlyout
                  projectRunConfigurationId={runConfiguration.projectRunConfigurationId}
                  askQuinnState={askQuinnState}
                  activeProjectModule={this.state.activeProjectModule}
                  isOnProviderClaimsInsightsTab={this.isOnProviderClaimsInsightsRoute}
                />
              )
              : this.props.virtualAssistantContext.isOpen
                && userCanAccessAIChatPanel(this.props.enabledFeatureFlags, this.props.user.roles) && (
                <AIChatPanel
                  projectRunConfigurationId={this.props.runConfiguration.projectRunConfigurationId}
                />
              )}
          </div>
        );
      } else {
        //TODO: Maybe we can push '/' to the router at this point?
        return null;
      }
    } else if (!error) {
      return (
        <SkeletonOverview projectModule={this.state.activeProjectModule} perspective={perspective} />
      );
    } else {
      // caught by error boundary
      throw { status: error.status, statusText: error.statusText, traceId: error.responseJSON?.traceId };
    }
  }

  private renderGapDensityMap(): JSX.Element | null {
    if (this.state.selectedSubmodule !== AdequacySubmoduleEnum.ViewMap
      || !this.shouldShowAdequacyTab
      || this.props.isDualMembershipMode) {
      return null;
    }

    return <GapDensityMap />;
  }

  private renderAgaStatus(user: UserInfo, props: CountyProjectOverviewProps): JSX.Element {
    if (!this.isUserAuthorizedOnAccuracyGapAnalysis(user)) return null;
    switch (this.props.runConfiguration.accuracyGapAnalysisStatus) {
      case AccuracyGapAnalysisStatus.Succeeded:
        return (
          <AgaAlert
            compareToRunConfiguration={this.props.compareToRunConfiguration}
            isDualMembershipMode={this.props.isDualMembershipMode}
            primaryConfiguration={this.props.primaryConfiguration}
            projectId={this.props.project.projectId}
            runConfiguration={this.props.runConfiguration}
            supplementaryConfiguration={this.props.supplementaryConfiguration}
            userRoles={user.roles}
          />
        );
      case AccuracyGapAnalysisStatus.Failed:
      case AccuracyGapAnalysisStatus.Canceled:
        return (
          <QESAlert type="danger" key="agaFailed" persist={true}>
            <strong>Warning!</strong> Accuracy Gap Analysis Failed.
            {this.props.enabledFeatureFlags.has(Features.USE_AGA_PARENT_ORCHESTRATION) &&
              <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
                <QESButton
                  className="ms-3"
                  disabled={this.state.disableAgaRunBanner}
                  onClick={() => {
                    this.setState({ disableAgaRunBanner: true });
                    this.props.rerunAccuracyGapAnalysis(props.project.projectId, props.runConfiguration.projectRunConfigurationId)
                  }}
                  qesTestId="project-overview__run-project--aga-failed"
                  qesType="secondary"
                >
                  <QesIcon iconName={QesIcons.RocketV2} isDecorative className="align-text-top" />
                  Run Again
                </QESButton>
              </RoleRestricted>
            }
          </QESAlert>
        );

      case AccuracyGapAnalysisStatus.Queued:
      case AccuracyGapAnalysisStatus.Analyzing:
        return (
          <QESAlert type="info" key="readonly" classNames="mt-3">
            Accuracy gap analysis is calculating.
          </QESAlert>
        );

      case AccuracyGapAnalysisStatus.Unknown:
      default:
        return null;
    }
  }

  private isUserAuthorizedOnAccuracyGapAnalysis(user: UserInfo): boolean {
    return (user.roles.includes(Roles.ACCURACY_LICENSE_TIER_1) || user.roles.includes(Roles.ACCURACY_LICENSE_TIER_2))
  }

  private renderFilters(filters: RegionFilterItem[], removeFilter: (urlPart: string) => void, removeCountyFilter: (countyFilter: string) => void) {
    if (!filters || filters.length === 0) {
      return null;
    }
    const filterEls: JSX.Element[] = filters.map((filter: RegionFilterItem, i: number) => {
      return (
        <span key={i} className={`overview-filter`} onClick={() => {
            if(filter.filterType === FilterType.County)
              removeCountyFilter(filter.filterValue);
            else
              removeFilter(filter.urlPart);
          }
          }>
          <span className="mr10">{filter.displayText}</span>
          <QesIcon iconName={QesIcons.XCircle} description="Remove this filter" className="align-text-top" />
        </span>
      );
    });

    return (
      <div className="row">
        <div className="col-12">
          <div className="overview-filters">
            <strong className="mr15">Current Filter:</strong>
            {filterEls}
          </div>
        </div>
      </div>
    );
  }

  private async renderStatus(
    user: UserInfo,
    isShared: boolean,
    isProjectRunning: boolean,
    processProviderChanges: (reprocessFailure: boolean) => Promise<void>,
    project: ProjectOptions,
    runConfiguration: ProjectRunConfigurationResult,
    runProject: () => void,
    requeueMarketAndProspectNCP: () => void,
    onPublished: () => void,
    isReadOnly: boolean,
    isEditRestricted: boolean,
  ) {
    const termAdmin = user.roles.indexOf(Roles.TERM_PROVIDER_ADMIN) > -1;
    const isRoleAuthorizationEnforced = this.props.enabledFeatureFlags.has(Features.ENFORCE_PROVIDER_CHANGE_ROLE_AUTHORIZATION);
    const projectionProcessor = isRoleAuthorizationEnforced
      ? user.roles.includes(Roles.PROJECTION_PROCESSOR) &&
        getProviderChangeRoleGate(user.roles, {
          hasExcludes: project.hasUnprocessedExcludedProviders,
          hasTargets: project.hasUnprocessedTargetProviders,
          hasTerminations: false,
        }).hasRequiredRoles
      : user.roles.indexOf(Roles.PROJECTION_PROCESSOR) > -1;
    const exceptionManager = user.roles.indexOf(Roles.EXCEPTION_MANAGER) > - 1;
    const showTermMessage = termAdmin && project.hasUnprocessedProviderTerminations && !project.isRunning && runConfiguration && runConfiguration.status !== ProjectRunConfigurationStatus.Failed;
    const showSpecialtyGroupExceptionMessage = exceptionManager && project.hasUnprocessedSpecialtyGroupExceptions && !project.isRunning && runConfiguration && runConfiguration.status !== ProjectRunConfigurationStatus.Failed;

    //don't show target message for accuracy-only projects
    const showProjectionMessage = (project.hasUnprocessedTargetProviders || project.hasUnprocessedExcludedProviders) && projectionProcessor && !project.isRunning && runConfiguration && runConfiguration.status !== ProjectRunConfigurationStatus.Failed;
    const renderProviderActionsFlyoutLink = this.renderProviderActionsFlyoutLink();
    const renderUnprocessedSpecialtyGroupExceptionsSidePanelLinkElement = this.renderUnprocessedSpecialtyGroupExceptionsChangesSidePanelLinkElement(project.projectId);

    if (isReadOnly) {
      if (!project.isRunning && runConfiguration && runConfiguration.status === ProjectRunConfigurationStatus.FailedProviderChanges) {
        return (
          <QESAlert type="danger" persist={true}>
            {isShared
              ? <>An error occurred during processing. We apologize for the inconvenience. Please contact your administrator.</>
              : <>Project failed while processing provider changes. If you are not sure you should reprocess this project, submit a ticket to QES Intake.
                {/* When role authorization is not enforced, this button renders unconditionally as it did
                    before the role requirements were introduced. When it is enforced, Term Provider Admins
                    remain eligible: the endpoint accepts that role, and reprocessing is only rejected if the
                    project has pending targets/exclusions the user lacks the matching role for. */}
                {(!isRoleAuthorizationEnforced || projectionProcessor || termAdmin) && (
                  <QESButton className="ms-3" qesType="secondary" onClick={() => processProviderChanges(true)} qesTestId="project-overview__process-provider-changes">
                    <QesIcon iconName={QesIcons.RocketV2} isDecorative className="align-text-top" />Re-Process Provider Changes
                  </QESButton>
                )}
              </>}
          </QESAlert>
        );
      }
      if (!project.isRunning && runConfiguration && runConfiguration.status === ProjectRunConfigurationStatus.FailedProspectMarketNcp) {
        return (
          <QESAlert type="danger" key="ncpFailed" persist={true}>
            <strong>Warning!</strong> Processing next closest providers failed.
            <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
              <QESButton className="ms-3" qesType="secondary" onClick={requeueMarketAndProspectNCP} qesTestId="project-overview__requeue-impact-ncp--impact-ncp-failed--readonly">
                <QesIcon iconName={QesIcons.RocketV2} isDecorative className="align-text-top" /> Run Again
              </QESButton>
            </RoleRestricted>
          </QESAlert>
        );
      }
      return (
        <QESAlert type="info" key="readonly" persist={true}>
          This project is in a read-only mode while processing updates. Some items may not appear on the trend chart while they are being worked on.
        </QESAlert>
      );
    }
    if (isEditRestricted) {
      const multiMessage: JSX.Element[] = [];
      if (!project.isRunning && runConfiguration && runConfiguration.status === ProjectRunConfigurationStatus.FailedProspectMarketNcp) {
        multiMessage.push(
          <QESAlert type="danger" key="ncpFailed" persist={true}>
            <strong>Warning!</strong> Processing next closest providers failed.
            <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
              <QESButton className="ms-3" qesType="secondary" onClick={requeueMarketAndProspectNCP} qesTestId="project-overview__requeue-impact-ncp--impact-ncp-failed--edit-restricted">
                <QesIcon iconName={QesIcons.RocketV2} isDecorative className="align-text-top" /> Run Again
              </QESButton>
            </RoleRestricted>
          </QESAlert>
        );
      } else {
        multiMessage.push(
          <QESAlert type="info" key="editrestricted" persist={true}>
            {`This project is in a read-only mode while processing Next Closest Prospect and Market Providers.`}
          </QESAlert>
        );
      }

      if (!isShared) {
        // build the not published banner and add it to the multimessage
        if (project.canPublish && runConfiguration && runConfiguration.datePublished === undefined) {
          multiMessage.push((
            <QESAlert type="warning" key="projectUnpublished" persist={true}>
              <strong>Warning!</strong> Your project results are unpublished.
              <FlyoutLink
                classNames="ms-3"
                content={{
                  body: this.getPublishSidePanel(project, runConfiguration, onPublished),
                  title: "Publish Options",
                }}
                testId="publish-project-flyout-link"
              >
                <QesIcon iconName={QesIcons.ThumbsUp} isDecorative className="me-1 align-text-top" />
                Publish
              </FlyoutLink>
            </QESAlert>
          ));
        }
      }

      if (multiMessage.length <= 1) {
        return multiMessage;
      }

      return (
        <QESAlertsContainer containerClass="mb-3">
          {multiMessage}
        </QESAlertsContainer>
      );
    }
    if (isShared) {
      if (runConfiguration) {
        const indeterminateStatusMessage = this.renderIndeterminateStatusMessage(project, runConfiguration.status);
        if (indeterminateStatusMessage) {
          return indeterminateStatusMessage;
        }

        const multiMessage: JSX.Element[] = [];
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.QueuedProviderChanges) {
          multiMessage.push(
            <QESAlert key="queued-changes" type="info" persist={true}>
              Queued to Process Provider Changes.
            </QESAlert>
          );
        }
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.ProcessingProviderChanges) {
          multiMessage.push(
            <QESAlert key="processing-changes" type="info" persist={true}>
              Processing Provider Changes.
            </QESAlert>
          );
        }
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.QueuedProspectMarketNcp) {
          multiMessage.push(
            <QESAlert key="queued-ncp" type="info" persist={true}>
              Queued to process Next Closest Providers.
            </QESAlert>
          );
        }
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.ProcessingProspectMarketNcp) {
          multiMessage.push(
            <QESAlert key="processing-ncp" type="info" persist={true}>
              Processing Next Closest Providers.
            </QESAlert>
          );
        }
        if (runConfiguration.status === ProjectRunConfigurationStatus.FailedProviderChanges) {
          multiMessage.push(
            <QESAlert key="failed" type="info" persist={true}>
              An error occurred during processing. We apologize for the inconvenience. Please contact your administrator.
            </QESAlert>
          );
        }
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.QueuedSpecialtyGroupExceptionAnalysis) {
          multiMessage.push(
            <QESAlert key="queued-exceptions" type="info" persist={true}>
              Queued to Recalculate Results for Specialty Group Exception Change.
            </QESAlert>
          );
        }
        if (isProjectRunning && runConfiguration.status === ProjectRunConfigurationStatus.ProcessingSpecialtyGroupExceptionAnalysis) {
          multiMessage.push(
            <QESAlert key="recalculating-exceptions" type="info" persist={true}>
              Recalculating Results for Specialty Group Exception Change.
            </QESAlert>
          );
        }
        if (showSpecialtyGroupExceptionMessage) {
          multiMessage.push(
            <RoleRestricted key="unprocessed-exceptions" allowedRoles={[Roles.EXCEPTION_MANAGER]}>
              <QESAlert type="warning" persist={true}>
                <strong> Warning!</strong> Your project has unprocessed specialty group exceptions.
                {renderUnprocessedSpecialtyGroupExceptionsSidePanelLinkElement}
              </QESAlert>
            </RoleRestricted>);
        }
        if (showTermMessage && showProjectionMessage) {
          multiMessage.push(
            <RoleRestricted key="unprocessed-terms-projections" requiredRoles={[Roles.PROJECTION_PROCESSOR, Roles.TERM_PROVIDER_ADMIN]}>
              <QESAlert type="warning" persist={true}>
                <strong>Warning!</strong> Your project has unprocessed provider terminations and unprocessed targeted or excluded providers. Process your pending actions to view the most up-to-date project data.
                {renderProviderActionsFlyoutLink}
              </QESAlert>
            </RoleRestricted>
          );
        } else if (showTermMessage) {
          multiMessage.push(
            <RoleRestricted key="unprocessed-terms" allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
              <QESAlert type="warning" persist={true}>
                <strong>Warning!</strong> Your project has unprocessed provider terminations. Process your pending actions to view the most up-to-date project data.
                {renderProviderActionsFlyoutLink}
              </QESAlert>
            </RoleRestricted>
          );
        } else if (showProjectionMessage) {
          multiMessage.push(
            <RoleRestricted key="unprocessed-projections" allowedRoles={[Roles.PROJECTION_PROCESSOR]}>
              <QESAlert type="warning" persist={true}>
                <strong>Warning!</strong> Your project has unprocessed targeted or excluded providers. Process your pending actions to view the most up-to-date project data.
                {renderProviderActionsFlyoutLink}
              </QESAlert>
            </RoleRestricted>
          );
        }
        if (multiMessage.length <= 1) {
          return multiMessage;
        } else if (multiMessage.length > 1) {
          return (
            <QESAlertsContainer containerClass="mb-3">
              {multiMessage}
            </QESAlertsContainer>
          );
        }
      }
    } else {
      if (!runConfiguration) {
        return (
          <QESAlert type="info" persist={true}>
            <strong>Thank you!</strong> Your project has been created, please run it.
            <QESButton className="ms-3" qesType="secondary" onClick={runProject} qesTestId="project-overview__run-project--no-config">
              <QesIcon iconName={QesIcons.RocketV2} isDecorative className="align-text-top" /> Run Now
            </QESButton>
          </QESAlert>
        );
      }

      // check for indeterminate state first and bail if it's in that state
      const indeterminateStatusMessage = this.renderIndeterminateStatusMessage(project, runConfiguration.status);
      if (indeterminateStatusMessage) {
        return indeterminateStatusMessage;
      }

      const multiMessage: JSX.Element[] = [];

      switch (runConfiguration.status) {
        case ProjectRunConfigurationStatus.Created:
          multiMessage.push((
            <QESAlert key="statusbanner" type="info" persist={true}>
              Your project has been created, is valid, and is ready to run.
              <QESButton className="ms-3" qesType="secondary" onClick={runProject} qesTestId="project-overview__run-project--status-created">
                <QesIcon iconName={QesIcons.RocketV2} isDecorative className="align-text-top" /> Run Now
              </QESButton>
            </QESAlert>
          ));
          break;
        case ProjectRunConfigurationStatus.Queued:
          multiMessage.push((
            <QESAlert type="info" key="statusbanner" persist={true}>
              Your project is currently queued to be run.
            </QESAlert>
          ));
          break;
        case ProjectRunConfigurationStatus.Analyzing:
        case ProjectRunConfigurationStatus.Aggregating:
          multiMessage.push((
            <QESAlert key="statusbanner" type="info" persist={true}>
              Your project is currently running.
            </QESAlert>
          ));
          break;
        // case ProjectRunConfigurationStatus.Canceled:
        // this was moved to a conditional below
        // break;
        case ProjectRunConfigurationStatus.Failed:
          multiMessage.push((
            <QESAlert type="warning" key="projectFailed" persist={true}>
              <strong>Warning!</strong> Your latest project run failed.
              <QESButton className="ms-3" qesType="secondary" onClick={runProject} qesTestId="project-overview__run-project--run-failed">
                <QesIcon iconName={QesIcons.RocketV2} isDecorative className="align-text-top" /> Run Again
              </QESButton>
            </QESAlert>
          ));
          break;
        case ProjectRunConfigurationStatus.FailedProspectMarketNcp:
          multiMessage.push((
            <QESAlert type="danger" key="ncpFailed" persist={true}>
              <strong>Warning!</strong> Processing next closest providers failed.
              <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
                <QESButton className="ms-3" qesType="secondary" onClick={requeueMarketAndProspectNCP} qesTestId="project-overview__requeue-impact-ncp--config-status-failed-impact-ncp">
                  <QesIcon iconName={QesIcons.RocketV2} isDecorative className="align-text-top" /> Run Again
                </QESButton>
              </RoleRestricted>
            </QESAlert>
          ))
          break;
        case ProjectRunConfigurationStatus.FailedProviderChanges:
          multiMessage.push((
            <QESAlert type="warning" key="providerChangesFiled" persist={true}>
              <strong>Warning!</strong> Processing provider changes failed.
            </QESAlert>
          ));
          break;
        case ProjectRunConfigurationStatus.Succeeded:
          // build the project change reason list and add it to the multimessage
          if (project.projectNeedsFullRun) {
            const list = project.projectChangeReasons.map((r) => <li key={r}>{translateProjectChangeReason(r)}</li>);
            multiMessage.push((
              <QESAlert
                collapseTitle={
                  <>
                    <strong>Warning!</strong> Your project results are out of date.
                    {/* HTML Syntax doesn't allow a button inside a button (QESAlert's collapse button),
                      so we get around it here with a span with role="button" and the properties that otherwise would
                      be native to a button (onClick, onKeyDown, tabIndex)
                      See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role for more
                  */}
                    <span
                      className="d-inline-block ms-3"
                      onClick={runProject}
                      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); runProject(); } }}
                      role="button"
                      tabIndex={0}
                      data-testid="project-overview__run-project--results-out-of-date"
                    >
                      <QesIcon iconName={QesIcons.RocketV2} isDecorative className="align-text-top" /> Run Now
                    </span>
                  </>
                }
                key="projectChanged"
                persist={true}
                type="warning"
              >
                <p className="pt5">The following changes have occurred since your project was last run:</p>
                <ul>
                  {list}
                </ul>
              </QESAlert>
            ));
          }

          if (project.showMotiveAppropriatenessScore &&
            !!project.templateId &&
            !!runConfiguration.adequacyTemplateCollectionId) {
            const templateInfo = await this.templateService.getTemplateById(runConfiguration.adequacyTemplateCollectionId, project.templateId, false);

            if (templateInfo.motiveCrosswalkCodeMapSetId === undefined || templateInfo.motiveCrosswalkCodeMapSetId === null) {
              multiMessage.push((
                <QESAlert type="warning" key="motiveReqd" persist={true}>
                  <strong>Warning!</strong> Motive data will not be available based on template configuration.
                </QESAlert>
              ));
            }
          }

          // build the not published banner and add it to the multimessage
          if (project.canPublish && runConfiguration && runConfiguration.datePublished === undefined) {
            multiMessage.push((
              <QESAlert type="warning" key="projectUnpublished" persist={true}>
                <strong>Warning!</strong> Your project results are unpublished.
                <FlyoutLink
                  classNames="ms-3"
                  content={{
                    body: this.getPublishSidePanel(project, runConfiguration, onPublished),
                    title: "Publish Options",
                  }}
                  testId="publish-project-flyout-link"
                  size="40"
                >
                  <QesIcon iconName={QesIcons.ThumbsUp} isDecorative className="me-1 align-text-top" />
                  Publish
                </FlyoutLink>
              </QESAlert>
            ));
          }
          break;
      }

      if (project.projectRunConfigurations.some((prc) => (prc.status === ProjectRunConfigurationStatus.Canceled && new Date(prc.dateCreated).getTime() === Math.max(...project.projectRunConfigurations.map((c) => new Date(c.dateCreated).getTime()))))) {
        let message = "Your latest project run was canceled.";
        if (runConfiguration.status === ProjectRunConfigurationStatus.Succeeded) {
          message += ` The data below is from your ${formatDateTime(runConfiguration.dateCreated)} run.`;
        } else if (project.projectRunConfigurations.filter((prc) => prc.status === ProjectRunConfigurationStatus.Succeeded).length === 0) {
          message += " This project has no successful runs to pull data from.";
        }
        multiMessage.push((
          <QESAlert type="warning" key="projectCancelled" persist={true}>
            <strong>Warning!</strong> {message}
            <QESButton className="ms-3" qesType="secondary" onClick={runProject} qesTestId="project-overview__run-project--run-canceled">
              <QesIcon iconName={QesIcons.RocketV2} isDecorative className="align-text-top" /> Run Again
            </QESButton>
          </QESAlert>
        ));
      }

      if (showTermMessage && showProjectionMessage) {
        multiMessage.push(
          <RoleRestricted requiredRoles={[Roles.PROJECTION_PROCESSOR, Roles.TERM_PROVIDER_ADMIN]} key="unprocessedTargetsAndTerminations">
            <QESAlert type="warning" persist={true}>
              <strong>Warning!</strong> Your project has unprocessed provider terminations and unprocessed targeted or excluded providers. Process your pending actions to view the most up-to-date project data.
              {renderProviderActionsFlyoutLink}
            </QESAlert>
          </RoleRestricted>,
        );
      } else if (showTermMessage) {
        multiMessage.push(
          <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]} key="unprocessedTerminations">
            <QESAlert type="warning" persist={true}>
              <strong>Warning!</strong> Your project has unprocessed provider terminations. Process your pending actions to view the most up-to-date project data.
              {renderProviderActionsFlyoutLink}
            </QESAlert>
          </RoleRestricted>,
        );
      } else if (showProjectionMessage) {
        multiMessage.push(
          <RoleRestricted allowedRoles={[Roles.PROJECTION_PROCESSOR]} key="unprocessedTargets">
            <QESAlert type="warning" persist={true}>
              <strong>Warning!</strong> Your project has unprocessed targeted or excluded providers. Process your pending actions to view the most up-to-date project data.
              {renderProviderActionsFlyoutLink}
            </QESAlert>
          </RoleRestricted>,
        );
      }
      if (showSpecialtyGroupExceptionMessage) {
        multiMessage.push(
          <RoleRestricted allowedRoles={[Roles.EXCEPTION_MANAGER]} key="unproccessedSpecialtyGroupExceptions">
            <QESAlert type="warning" persist={true}>
              <strong> Warning!</strong> Your project has unprocessed specialty group exceptions.
              {renderUnprocessedSpecialtyGroupExceptionsSidePanelLinkElement}
            </QESAlert>
          </RoleRestricted>);
      }

      if (multiMessage.length <= 1) {
        return multiMessage;
      }

      return (
        <QESAlertsContainer containerClass="mb-3">
          {multiMessage}
        </QESAlertsContainer>
      );
    }

    return null;
  }

  private renderProviderActionsFlyoutLink = () => {
    return (
      <ProviderActionsDrawerFlyout
        classNames="ml10 qes-button-secondary provider-actions-link"
        testId="county-project-overview__provider-actions-flyout-link"
        resetBanner={this.resetStatusBanners}
      >
        View and Process
      </ProviderActionsDrawerFlyout>
    );
  }

  private renderUnprocessedSpecialtyGroupExceptionsChangesSidePanelLinkElement = (projectId: number) => {
    return (<PendingUnprocessedSpecialtyGroupExceptionsChangesSidePanelLink
      projectId={projectId}
    />);
  }

  private renderIndeterminateStatusMessage(project: ProjectOptions, runConfigurationStatus: ProjectRunConfigurationStatus) {
    if (runConfigurationStatus === ProjectRunConfigurationStatus.Unknown || !project || !project.resource) {
      return (
        <QESAlert type="warning" persist={true}>
          <strong>Uh oh!</strong> Something might be wrong with your project. It&apos;s in an indeterminate state.
        </QESAlert>
      );
    }

    return null;
  }

  private getPublishSidePanel = (project: ProjectOptions, runConfiguration: ProjectRunConfiguration, onPublished: () => void) => {
    return (
      <Publish
        project={project}
        runConfigurationId={runConfiguration.projectRunConfigurationId}
        agaStatus={runConfiguration.accuracyGapAnalysisStatus}
        onPublished={onPublished}
      />
    );
  }

  /** Changes URL and active module state, as well as handling bidirectional dual membership sync between Network Integrity
   * and Adequacy.
   * @param moduleSubpath - Optional subpath to add to module URL (should include leading "/").
   */
  private setActiveProjectModule = (projectModule: ProjectModule, replace = false, moduleSubpath = "") => {
    let newPath = "";

    if (
      projectModule === "Summary" ||
      projectModule === "Mapping" ||
      projectModule === "Claims-Activity" ||
      projectModule === "Claims-Insights" ||
      projectModule === "Provider-Hub" ||
      projectModule === "Network-Integrity" ||
      projectModule === "Network-Performance") {
      newPath = this.getNewPath(this.props.location, projectModule.toLowerCase(), true);
    } else {
      newPath = this.getNewPath(this.props.location, projectModule.toLowerCase());
    }

    // Bidirectional dual membership sync between Network Integrity and Adequacy.
    const hasDualMembership = !!this.props.runConfiguration?.companionProjectRunConfigurationId;
    if (hasDualMembership) {
      const currentModule = this.props.location.pathname.split("/")[3];
      if (currentModule === this.networkIntegrityModulePath) {
        // Leaving NI: sync the NI membership selection back to the project context
        // so that Adequacy (and other tabs) reflect the membership the user chose on NI.
        // However, if Adequacy had "Both" memberships selected, preserve that.
        if (!this.props.isDualMembershipMode) {
          const isSecondary = getLastAppliedMembership() === NetworkIntegrityMembership.Secondary;
          const targetType = isSecondary
            ? ProjectRunConfigurationType.DualMembershipSupplemental
            : ProjectRunConfigurationType.DualMembershipPrimary;
          if (this.props.runConfiguration.projectRunConfigurationType !== targetType) {
            this.props.setDualMembershipMode(false, targetType);
          }
        }
      } else if (projectModule.toLowerCase() === this.networkIntegrityModulePath) {
        // Entering NI: sync the current project context membership to NI's module-level state
        // so that NI initializes with the membership the user currently has on Adequacy.
        // Provider type defaults to the generic servicing view; membership is carried separately.
        const isSecondary = this.props.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental;
        setLastAppliedMembership(isSecondary ? NetworkIntegrityMembership.Secondary : NetworkIntegrityMembership.Primary);
        setLastAppliedProviderType(NetworkIntegrityProviderTypes.Servicing);
      }
    }

    newPath += moduleSubpath;

    if (replace) {
      this.props.history.replace(newPath);
    } else {
      this.props.history.push(newPath);
    }

    this.setState({
      activeProjectModule: projectModule,
    });
  }

  private handleShowCharts = () => {
    this.setState((prevState) => ({
      ...prevState,
      showCharts: !prevState.showCharts,
    }));
  }

  private onSubmoduleChange = (newView: AdequacySubmoduleEnum) => {
    this.setState({
      selectedSubmodule: newView,
    })
  }
}

interface SkeletonOverviewProps {
  error?: JQuery.jqXHR;
  perspective?: ProjectPerspective;
  projectModule: ProjectModule;
}

const SkeletonOverview: React.FC<SkeletonOverviewProps> = (props) => {
  let alert: JSX.Element = null;

  if (props.error) {
    // Hopefully we'll eventually get the Http status code on the error
    // and be able to do a switch on the error code here to display a
    // more accurate message
    alert = (
      <div className="row mt20">
        <div className="col-12">
          <QESAlert type="danger" persist={true}>
            {getGenericErrorMessage()}
          </QESAlert>
        </div>
      </div>
    );

    throw props.error;
  }

  if (props.projectModule === "Network-Integrity") {
    return <NetworkIntegrityCountyProjectSkeleton alert={alert} />;
  }

  return <AdequacyCountyProjectSkeleton alert={alert} perspective={props.perspective} />;
}

interface SkeletonProjectPageProps {
  alert?: JSX.Element;
  children?: React.ReactNode;
}

const SkeletonProjectPage: React.FC<SkeletonProjectPageProps> = ({ alert, children }) => {
  return (
    <Page documentTitle="Loading">
      <PageInner>
        <PageSection>
          <div className="skeleton shimmer">
            <SkeletonHeader />
            {alert}
            <div className="row">
              <div className="col-12" />
            </div>
            <div className="row">
              <div className="col-12">
                <div className="tabs-style-2">
                  <ul className="nav nav-tabs">
                    <li className="nav-item">
                      <a className="nav-link" href="#" onClick={preventDefaultMouseEvent} role="tab">
                        <div className="bone">Network Integrity</div>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a className="nav-link" href="#" onClick={preventDefaultMouseEvent} role="tab">
                        <div className="bone">Adequacy</div>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a className="nav-link" href="#" onClick={preventDefaultMouseEvent} role="tab">
                        <div className="bone">Maps</div>
                      </a>
                    </li>
                    <li className="nav-item">
                      <a className="nav-link" href="#" onClick={preventDefaultMouseEvent} role="tab">
                        <div className="bone">Network Performance</div>
                      </a>
                    </li>
                  </ul>
                </div>
                {children}
              </div>
            </div>
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
}

interface AdequacyCountyProjectSkeletonProps {
  alert?: JSX.Element;
  perspective?: ProjectPerspective;
}

const AdequacyCountyProjectSkeleton: React.FC<AdequacyCountyProjectSkeletonProps> = ({ alert, perspective }) => {
  return (
    <SkeletonProjectPage alert={alert}>
      <div className="container-fluid">
        <div className="row ptb15 overview-options">
          <div className="col-12">
            <div className="pull-left bone"><QesIcon iconName={QesIcons.PencilV1} isDecorative className="align-text-top" /> Project Options</div>
            <div className="pull-right d-none d-sm-block">
              <span className="bone">
                <QesIcon iconName={QesIcons.SlidersV4} isDecorative className="align-text-top" /> Display Options
              </span>
              <SkeletonIconToggle className="ml10-imp pl10-imp vertical-separator pr15">
                <QesIcon iconName={QesIcons.BarChartV7} isDecorative />
              </SkeletonIconToggle>
            </div>
            <div className="clearfix" />
          </div>
        </div>
        <SkeletonCharts />
        <div className="row">
          {perspective !== ProjectPerspective.All && (
            <div className="col">
              <div className="tabs-style-2">
                <div className="tab-content overview-tab-content">
                  <div className="tab-pane in active">
                    <div className="details-filters d-none d-md-block">
                      <div>
                        <div className="btn filter-button bone">&nbsp;</div>
                      </div>
                      <div>
                        <SkeletonIconToggle text="All" />
                        <SkeletonIconToggle text="Gaps" />
                        <SkeletonIconToggle text="Exceptions" />
                        <SkeletonIconToggle className="pl20-imp vertical-separator" text="Opportunity" />
                      </div>
                    </div>
                    <SkeletonTable />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </SkeletonProjectPage>
  );
}

interface NetworkIntegrityCountyProjectSkeletonProps {
  alert?: JSX.Element;
}

const NetworkIntegrityCountyProjectSkeleton: React.FC<NetworkIntegrityCountyProjectSkeletonProps> = ({ alert }) => {
  return (
    <SkeletonProjectPage alert={alert}>
      <div className="ni-filters__container ptb15">
        <div className="d-flex flex-wrap align-items-center ni-filters__row">
          <div className="bone" style={{ height: "2rem", width: "50%" }} />
          <button className="bone qes-button qes-button-primary ni-filters__apply-button" style={{ marginTop: 0 }}>Apply</button>
          <button className="bone qes-button qes-button-secondary ni-filters__reset-button" style={{ marginTop: 0 }}>Reset All</button>
        </div>
        <div>
          <div className="bone" style={{ height: "2rem", marginTop: "-0.25rem", width: "100%" }} />
        </div>
      </div>

      <NetworkIntegrityWidgetContainer>
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="col-12 col-sm-6 col-lg-4 col-xl-3 col-xxl-2dot4 col-xxxl-2 col-4xl-1dot7 col-5xl-1dot5 col-6xl-1dot33 ni-widget">
            <div className="tile-item ellipsis">
              <div className="d-flex flex-column box-style-1 white-bg project-tile-top ni-widget-content">
                <div className="d-flex flex-column align-items-start">
                  <h3 className="lead font-weight-bold mtb0 ni-title">
                    <span className="bone">Widget&nbsp;Title</span>
                  </h3>
                </div>
                <div className="ni-widget-content-body">
                  <div className="bone" style={{ height: "224px" }} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </NetworkIntegrityWidgetContainer>
    </SkeletonProjectPage>
  );
}